import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Tab,
  Tabs,
  Skeleton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Tag as TagIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Notes as NotesIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { jobService, Job, UpdateJobDto, JobType, Priority } from '../services/jobService';
import { customerService, Customer } from '../services/customerService';
import { businessService, Business } from '../services/businessService';
import { appointmentService, Appointment } from '../services/appointmentService';
import { appointmentTypeService, AppointmentType } from '../services/appointmentTypeService';
import { formService, Form } from '../services/formService';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';
import { offlineService } from '../services/offlineService';
import { indexedDbService } from '../services/indexedDbService';
import { PDFService } from '../services/pdfService';
import BaseDialog from '../components/common/BaseDialog';
import CustomerDetailsCard from '../components/common/CustomerDetailsCard';
import JobForm from '../components/common/JobForm';
import FormRenderer from '../components/forms/FormRenderer';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} style={{ paddingTop: '16px' }}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const JobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useBusinessTheme();
  const [job, setJob] = useState<Job | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [formResponses, setFormResponses] = useState<{ [formId: number]: any }>({});
  const [allJobFormResponses, setAllJobFormResponses] = useState<any[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [loadingAppointmentTypes, setLoadingAppointmentTypes] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);
  const [loadingFormResponses, setLoadingFormResponses] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isOffline, setIsOffline] = useState(!offlineService.getOnlineStatus());
  const [formData, setFormData] = useState<UpdateJobDto>({
    id: '',
    customerId: '',
    assignedSalesPersonId: user?.id || '',
    type: JobType.Consultation,
    estimatedValue: 0,
    priority: Priority.Medium
  });

  // Load forms, business, and appointment types immediately when component mounts (don't wait for job)
  // This runs as soon as user?.businessId is available, which should be immediately
  useEffect(() => {
    if (user?.businessId) {
      // Load forms, business, and appointment types in parallel - these don't depend on the job
      // Start loading immediately to ensure tabs appear together
      loadForms();
      loadBusiness();
      loadAppointmentTypes();
    }
  }, [user?.businessId]);

  // Load job when id is available (runs in parallel with forms/business loading)
  useEffect(() => {
    if (id) {
      loadJob();
    }
  }, [id]);

  // Offline status monitoring
  useEffect(() => {
    const handleConnectivityChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      setIsOffline(!customEvent.detail.isOnline);
    };

    // Listen for offline/online events
    window.addEventListener('connectivity-changed', handleConnectivityChange);
    
    // Also check offline service status periodically to catch any missed updates
    const checkOfflineStatus = () => {
      const isOnline = offlineService.getOnlineStatus();
      setIsOffline(!isOnline);
    };
    
    // Check immediately and then every 2 seconds
    checkOfflineStatus();
    const interval = setInterval(checkOfflineStatus, 2000);
    
    return () => {
      window.removeEventListener('connectivity-changed', handleConnectivityChange);
      clearInterval(interval);
    };
  }, []);

  // Load form responses when forms are loaded and a form tab is active
  useEffect(() => {
    if (forms.length > 0 && id && tabValue >= 4) {
      const formIndex = tabValue - 4;
      if (formIndex >= 0 && formIndex < forms.length) {
        const form = forms[formIndex];
        if (!formResponses[form.id] && !loadingFormResponses) {
          loadFormResponses(form.id);
        }
      }
    }
  }, [forms, tabValue, id]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadJob = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Try API first, fallback to IndexedDB if it fails
      try {
        console.log('[JobDetailPage] Attempting to load job from API');
        const jobData = await jobService.getJob(id);
        setJob(jobData);
        
        // Load customer details
        if (jobData.customerId) {
          loadCustomer(jobData.customerId);
        }
      } catch (apiError: any) {
        console.log('[JobDetailPage] API failed, trying IndexedDB fallback:', apiError.message);
        
        // API failed, try IndexedDB
        const jobData = await indexedDbService.getJob(id);
        if (jobData) {
          console.log('[JobDetailPage] Successfully loaded job from IndexedDB');
          setJob(jobData);
          
          // Load customer details from IndexedDB
          if (jobData.customerId) {
            loadCustomerOffline(jobData.customerId);
          }
        } else {
          setError('Job not found in offline data. Please check your internet connection and try again.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomer = async (customerId: string) => {
    try {
      const customerData = await customerService.getCustomer(customerId);
      setCustomer(customerData);
    } catch (err: any) {
      console.error('Failed to load customer from API, trying IndexedDB:', err);
      // Fallback to IndexedDB
      try {
        const customerData = await indexedDbService.getCustomer(customerId);
        if (customerData) {
          setCustomer(customerData);
        }
      } catch (indexedDbErr: any) {
        console.error('Failed to load customer from IndexedDB:', indexedDbErr);
      }
    }
  };

  const loadCustomerOffline = async (customerId: string) => {
    try {
      const customerData = await indexedDbService.getCustomer(customerId);
      if (customerData) {
        setCustomer(customerData);
      }
    } catch (err: any) {
      console.error('Failed to load customer from IndexedDB:', err);
    }
  };

  const loadBusiness = async () => {
    try {
      const businessData = await businessService.getBusinesses();
      if (businessData && businessData.length > 0) {
        setBusiness(businessData[0]);
      }
    } catch (err: any) {
      console.error('Failed to load business:', err);
    }
  };

  const loadAppointmentTypes = async () => {
    try {
      setLoadingAppointmentTypes(true);
      const appointmentTypesData = await appointmentTypeService.getAppointmentTypes(user?.businessId || undefined);
      setAppointmentTypes(appointmentTypesData);
    } catch (err: any) {
      console.error('Failed to load appointment types:', err);
    } finally {
      setLoadingAppointmentTypes(false);
    }
  };

  const loadForms = async () => {
    try {
      setLoadingForms(true);
      const formsData = await formService.getForms();
      setForms(formsData);
    } catch (err: any) {
      console.error('Failed to load forms:', err);
      setError(err.message || 'Failed to load forms');
    } finally {
      setLoadingForms(false);
    }
  };

  const loadAppointments = async () => {
    if (!id) return;
    
    try {
      setLoadingAppointments(true);
      
      // Check if we're offline
      const isCurrentlyOffline = !offlineService.getOnlineStatus();
      
      if (isCurrentlyOffline) {
        console.log('[JobDetailPage] Loading appointments from IndexedDB (offline mode)');
        
        // Load appointments from IndexedDB and filter by jobId
        const allAppointments = await indexedDbService.getAppointmentsForBusiness(user?.businessId || '');
        const jobAppointments = allAppointments.filter(appointment => appointment.jobId === id);
        
        console.log(`[JobDetailPage] Found ${jobAppointments.length} appointments for job ${id} in IndexedDB`);
        setAppointments(jobAppointments);
      } else {
        // Online mode - use API
        const appointmentsData = await appointmentService.getAppointmentsByJobId(id);
        setAppointments(appointmentsData);
      }
    } catch (err: any) {
      console.error('Failed to load appointments:', err);
      setError(err.message || 'Failed to load appointments');
    } finally {
      setLoadingAppointments(false);
    }
  };

  const handleEditClick = () => {
    if (!job) return;
    
    // Convert string enum values to numeric enum values for form
    const convertJobType = (type: JobType | string): JobType => {
      if (typeof type === 'string') {
        const stringToEnum: { [key: string]: JobType } = {
          'KitchenDesign': JobType.KitchenDesign,
          'KitchenRemodel': JobType.KitchenRemodel,
          'NewKitchen': JobType.NewKitchen,
          'Consultation': JobType.Consultation,
          'Renovation': JobType.Renovation,
          'Other': JobType.Other
        };
        return stringToEnum[type] || JobType.Consultation;
      }
      return type as JobType;
    };
    
    const convertPriority = (priority: Priority | string): Priority => {
      if (typeof priority === 'string') {
        const stringToEnum: { [key: string]: Priority } = {
          'Low': Priority.Low,
          'Medium': Priority.Medium,
          'High': Priority.High,
          'Urgent': Priority.Urgent
        };
        return stringToEnum[priority] || Priority.Medium;
      }
      return priority as Priority;
    };
    
    setFormData({
      id: job.id,
      customerId: job.customerId,
      assignedSalesPersonId: job.assignedSalesPersonId,
      jobAddress: job.jobAddress || '',
      type: convertJobType(job.type),
      estimatedValue: job.estimatedValue,
      actualValue: job.actualValue,
      priority: convertPriority(job.priority),
      expectedCompletionDate: job.expectedCompletionDate,
      notes: job.notes || ''
    });

    setIsEditDialogOpen(true);
  };

  const handleUpdateJob = async () => {
    if (!job) return;
    
    try {
      // Clean up form data - convert empty strings to undefined for optional fields
      const cleanedFormData = {
        ...formData,
        jobAddress: formData.jobAddress || undefined,
        expectedCompletionDate: formData.expectedCompletionDate || undefined,
        notes: formData.notes || undefined
      };
      
      await jobService.updateJob(cleanedFormData);
      setIsEditDialogOpen(false);
      setSuccess('Job updated successfully');
      setError(null);
      loadJob();
    } catch (err: any) {
      if (err.response?.data?.errors) {
        const validationErrors = Object.entries(err.response.data.errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
        setError(`Validation errors:\n${validationErrors}`);
      } else {
        setError(err.message || 'Failed to update job');
      }
      setSuccess(null);
    }
  };

  const handleDeleteJob = async () => {
    if (!job || !window.confirm('Are you sure you want to delete this job?')) return;
    
    try {
      await jobService.deleteJob(job.id);
      navigate('/crm/jobs');
    } catch (err: any) {
      setError(err.message || 'Failed to delete job');
    }
  };

  const handleBack = () => {
    navigate('/crm/jobs');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Load appointments when switching to appointments tab
    if (newValue === 1 && appointments.length === 0 && !loadingAppointments) {
      loadAppointments();
    }
    
    // Always reload all form responses when switching to documents tab to get latest data
    if (newValue === 3 && !loadingDocuments && id) {
      loadAllFormResponses();
    }
    
    // Load form responses when switching to a form tab
    if (newValue >= 4 && id) {
      const formIndex = newValue - 4;
      if (formIndex >= 0 && formIndex < forms.length) {
        const form = forms[formIndex];
        if (!formResponses[form.id] && !loadingFormResponses) {
          loadFormResponses(form.id);
        }
      }
    }
  };

  const loadAllFormResponses = async () => {
    if (!id) return;
    
    try {
      setLoadingDocuments(true);
      const responses = await formService.getJobFormResponses(id);
      setAllJobFormResponses(responses);
    } catch (err: any) {
      console.error('Failed to load all form responses:', err);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const loadFormResponses = async (formId: number): Promise<{ [key: string]: any } | undefined> => {
    if (!id) return undefined;
    
    try {
      setLoadingFormResponses(true);
      
      // Get all job form responses
      const responses = await formService.getJobFormResponses(id);
      
      // Filter responses for this form and transform to form data
      const form = forms.find(f => f.id === formId);
      if (form) {
        // Filter responses by form - need to check if response belongs to this form
        // Responses have FormFieldId, so we need to check if that field belongs to this form
        const formFieldIds = new Set<number>();
        form.formSections?.forEach(section => {
          section.formFields?.forEach(field => {
            formFieldIds.add(field.id);
          });
        });
        
        const formResponsesForForm = responses.filter(response => 
          formFieldIds.has(response.formFieldId)
        );
        
        // Transform to form data format
        const formData = formService.transformJobFormResponsesToFormData(
          formResponsesForForm,
          form
        );
        
        setFormResponses(prev => ({
          ...prev,
          [formId]: formData
        }));
        
        return formData;
      }
    } catch (err: any) {
      console.error('Failed to load form responses:', err);
      // Don't show error to user, just log it
    } finally {
      setLoadingFormResponses(false);
    }
    
    return undefined;
  };

  // Get appointment type name by ID
  const getAppointmentTypeName = (appointmentTypeId: string): string => {
    const appointmentType = appointmentTypes.find(type => type.id === appointmentTypeId);
    return appointmentType ? appointmentType.name : 'Unknown Type';
  };

  // Generate document filename using the same format as FormRenderer
  const generateDocumentFilename = (form: Form): string => {
    const sanitizedFormName = form.description.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const sanitizedJobNumber = job?.jobNumber ? job.jobNumber.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : 'job';
    return `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;
  };

  // Get forms that have been filled out (have responses) and are printable
  const getFormsWithResponses = (): Form[] => {
    if (allJobFormResponses.length === 0) return [];
    
    // Create a map of form IDs to their field IDs
    const formFieldIdMap = new Map<number, Set<number>>();
    forms.forEach(form => {
      const fieldIds = new Set<number>();
      form.formSections?.forEach(section => {
        section.formFields?.forEach(field => {
          fieldIds.add(field.id);
        });
      });
      formFieldIdMap.set(form.id, fieldIds);
    });
    
    // Find forms that have at least one response and are printable
    const formsWithResponses: Form[] = [];
    forms.forEach(form => {
      // Only include forms that are printable
      if (!form.isPrintable) return;
      
      const fieldIds = formFieldIdMap.get(form.id);
      if (fieldIds) {
        const hasResponse = allJobFormResponses.some(response => 
          fieldIds.has(response.formFieldId)
        );
        if (hasResponse) {
          formsWithResponses.push(form);
        }
      }
    });
    
    // Sort by DisplayOrder
    return formsWithResponses.sort((a, b) => a.displayOrder - b.displayOrder);
  };

  const handlePrintDocument = async (form: Form) => {
    if (!id || !job) return;
    
    try {
      setError(null);
      
      // Always reload form responses to ensure we have the latest data
      const formData = await loadFormResponses(form.id) || {};
      
      // Debug: Log to verify data is loaded
      console.log('Printing document for form:', form.description);
      console.log('Form data loaded:', formData);
      console.log('Form data keys:', Object.keys(formData));
      console.log('Form data has values:', Object.keys(formData).length > 0);
      
      // Get form sections
      const formSections = form.formSections || [];
      
      // Generate filename
      const sanitizedFormName = form.description.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      const sanitizedJobNumber = job.jobNumber ? job.jobNumber.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : 'job';
      const filename = `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;
      
      // Parse PDF configuration from form if available
      let pdfConfig: any = {};
      if (form.pdfConfigurationJson) {
        try {
          pdfConfig = JSON.parse(form.pdfConfigurationJson);
        } catch (error) {
          console.warn('Failed to parse PDF configuration JSON:', error);
        }
      }
      
      // Prepare header data for placeholder replacement (e.g., {{DisplayName}})
      const headerData: { [key: string]: string | number | boolean | null | undefined } = {
        DisplayName: job.assignedSalesPersonName || ''
      };
      
      // Generate PDF blob
      const pdfBytes = await PDFService.generateEditablePDFBlob(
        formData,
        form.description,
        formSections,
        {
          filename,
          headerImageBase64: form.headerImageBase64,
          headerImageUrl: form.headerImageBase64 ? undefined : '/images/kitchen-express-header.png',
          headerConfigurationJson: form.headerConfigurationJson,
          headerHeight: pdfConfig.headerHeight ?? 100,
          pageMargin: pdfConfig.pageMargin ?? 15,
          headerData,
          defaultFontSize: pdfConfig.defaultFontSize,
          labelFontSize: pdfConfig.labelFontSize,
          multilineFontSize: pdfConfig.multilineFontSize,
          sectionTitleFontSize: pdfConfig.sectionTitleFontSize,
          fieldSpacing: pdfConfig.fieldSpacing,
          sectionSpacing: pdfConfig.sectionSpacing,
          checkboxListSpacing: pdfConfig.checkboxListSpacing,
          sectionPadding: pdfConfig.sectionPadding,
          fieldHeight: pdfConfig.fieldHeight,
          showSectionBorders: pdfConfig.showSectionBorders
        }
      );
      
      // Create blob and open in new tab
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Open PDF in new tab - user can print from there
      window.open(url, '_blank');
      
      // Clean up URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);
    } catch (err: any) {
      console.error('Error printing document:', err);
      setError(err.message || 'Failed to generate PDF for printing');
    }
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    const appointmentDate = new Date(appointment.startTime);
    navigate('/calendar', { 
      state: { 
        date: appointmentDate.toISOString(),
        appointmentId: appointment.id 
      } 
    });
  };

  if (loading) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header skeleton */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box>
              <Skeleton variant="text" width={200} height={40} />
              <Skeleton variant="text" width={150} height={24} />
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Skeleton variant="rectangular" width={100} height={36} />
            <Skeleton variant="rectangular" width={100} height={36} />
          </Box>
        </Box>

        {/* Tabs skeleton */}
        <Paper sx={{ backgroundColor: theme.accentColor, mb: 3, borderRadius: 0 }}>
          <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
            <Skeleton variant="rectangular" width={100} height={48} />
            <Skeleton variant="rectangular" width={120} height={48} />
            <Skeleton variant="rectangular" width={140} height={48} />
            <Skeleton variant="rectangular" width={120} height={48} />
            <Skeleton variant="rectangular" width={100} height={48} />
          </Box>
        </Paper>

        {/* Content skeleton */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: theme.accentColor }}>
              <CardContent>
                <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
                <Divider sx={{ mb: 2 }} />
                {Array.from({ length: 8 }).map((_, index) => (
                  <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                    <Box flex={1}>
                      <Skeleton variant="text" width={100} height={16} />
                      <Skeleton variant="text" width={200} height={24} />
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: theme.accentColor }}>
              <CardContent>
                <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
                <Divider sx={{ mb: 2 }} />
                {Array.from({ length: 4 }).map((_, index) => (
                  <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                    <Box flex={1}>
                      <Skeleton variant="text" width={100} height={16} />
                      <Skeleton variant="text" width={200} height={24} />
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error && !job) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Jobs
        </Button>
      </Box>
    );
  }

  if (!loading && !job) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Job not found
        </Alert>
        <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Jobs
        </Button>
      </Box>
    );
  }

  // Type guard: if we've passed all checks, job should exist
  if (!job) {
    return null;
  }

  // Build the title string
  const getPageTitle = () => {
    const parts: string[] = [];
    if (job.jobAddress) {
      // Get just the first part of the address (before first comma or newline)
      const firstPart = job.jobAddress.split(',')[0].split('\n')[0].trim();
      parts.push(firstPart);
    }
    if (customer) {
      parts.push(`${customer.firstName} ${customer.lastName}`);
    }
    if (parts.length === 0) {
      return job.jobNumber;
    }
    return parts.join(' - ');
  };

  const pageTitle = getPageTitle();

  return (
    <>
      <Helmet>
        <title>Job: {pageTitle} | MindShare Calendar</title>
      </Helmet>

      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton 
              onClick={handleBack} 
              color="primary"
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1">
                {pageTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {job.jobNumber}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
              disabled={isOffline}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteJob}
              disabled={isOffline}
            >
              Delete
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ backgroundColor: theme.accentColor, mb: 3, borderRadius: 0 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="job detail tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Overview" />
            <Tab label="Appointments" />
            <Tab label="Communication" />
            <Tab label="Documents" />
            {loadingForms ? (
              // Show skeleton tabs while forms are loading
              <>
                {[1, 2, 3].map((index) => (
                  <Tab
                    key={`skeleton-${index}`}
                    disabled
                    label={
                      <Skeleton
                        variant="text"
                        width={80}
                        height={20}
                        sx={{ display: 'inline-block' }}
                      />
                    }
                  />
                ))}
              </>
            ) : (
              forms.map((form, index) => (
                <Tab key={form.id} label={form.description} />
              ))
            )}
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Job Details Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Job Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <TagIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Job Number
                      </Typography>
                      <Typography variant="body1">
                        {job.jobNumber}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <WorkIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Type
                      </Typography>
                      <Typography variant="body1">
                        {jobService.getJobTypeDisplayName(job.type)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <WorkIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Box>
                        <Chip
                            label={job.statusName}
                            size="small"
                            style={{ backgroundColor: job.statusColor, color: 'white' }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <WorkIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Priority
                      </Typography>
                      <Box>
                        <Chip
                            label={jobService.getPriorityDisplayName(job.priority)}
                            size="small"
                            style={{ backgroundColor: jobService.getPriorityColor(job.priority), color: 'white' }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <PersonIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Assigned To
                      </Typography>
                      <Typography variant="body1">
                        {job.assignedSalesPersonName}
                      </Typography>
                    </Box>
                  </Box>

                  {job.jobAddress && (
                    <Box display="flex" alignItems="flex-start" sx={{ mb: 2 }}>
                      <LocationIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary', mt: 0.5 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Job Address
                        </Typography>
                        <Typography variant="body1">
                          {job.jobAddress}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <MoneyIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Estimated Value
                      </Typography>
                      <Typography variant="body1">
                        {jobService.formatCurrency(job.estimatedValue)}
                      </Typography>
                    </Box>
                  </Box>

                  {job.actualValue && (
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                      <MoneyIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Actual Value
                        </Typography>
                        <Typography variant="body1">
                          {jobService.formatCurrency(job.actualValue)}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <CalendarIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Created Date
                      </Typography>
                      <Typography variant="body1">
                        {new Date(job.createdDate).toLocaleDateString('en-GB')}
                      </Typography>
                    </Box>
                  </Box>

                  {job.expectedCompletionDate && (
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                      <CalendarIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Expected Completion
                        </Typography>
                        <Typography variant="body1">
                          {new Date(job.expectedCompletionDate).toLocaleDateString('en-GB')}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {job.notes && (
                    <Box display="flex" alignItems="flex-start" sx={{ mb: 2 }}>
                      <NotesIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary', mt: 0.5 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Notes
                        </Typography>
                        <Typography variant="body1">
                          {job.notes}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Customer Details Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Customer Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {customer ? (
                    <CustomerDetailsCard customer={customer} />
                  ) : (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Card sx={{ backgroundColor: theme.accentColor }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Appointments</Typography>
              
              {loadingAppointments ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress />
                </Box>
              ) : appointments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No appointments found for this job.
                </Typography>
              ) : (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {appointments.map((appointment) => (
                    <Grid item xs={12} key={appointment.id}>
                      <Card 
                        variant="outlined"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                        onClick={() => handleAppointmentClick(appointment)}
                      >
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1}>
                              <Typography variant="h6" gutterBottom>
                                {appointment.title}
                              </Typography>

                              <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                                <WorkIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {getAppointmentTypeName(appointment.appointmentTypeId)}
                                </Typography>
                              </Box>

                              {appointment.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {appointment.description}
                                </Typography>
                              )}
                              
                              <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  {new Date(appointment.startTime).toLocaleString('en-GB')} - {new Date(appointment.endTime).toLocaleString('en-GB')}
                                </Typography>
                              </Box>
                              
                              {appointment.address && (
                                <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                                  <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                  <Typography variant="body2">
                                    {appointment.address}
                                  </Typography>
                                </Box>
                              )}
                              
                              {appointment.customerName && (
                                <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                                  <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                  <Typography variant="body2">
                                    {appointment.customerName}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Card sx={{ backgroundColor: theme.accentColor }}>
            <CardContent>
              <Typography variant="h6">Communication</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Communication logs for this job will be displayed here.
              </Typography>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Card sx={{ backgroundColor: theme.accentColor }}>
            <CardContent>
              {loadingDocuments ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {(() => {
                    const formsWithResponses = getFormsWithResponses();
                    
                    if (formsWithResponses.length === 0) {
                      return (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          No forms have been filled out yet. Documents will appear here once forms are completed or when a document is uploaded.
                        </Typography>
                      );
                    }
                    
                    return (
                      <Grid container spacing={0} sx={{ mt: 1 }}>
                        {formsWithResponses.map((form) => {
                          const documentName = generateDocumentFilename(form);
                          return (
                            <Grid item xs={6} sm={4} md={3} lg={2} key={form.id}>
                              <Box
                                onClick={() => handlePrintDocument(form)}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  p: 2,
                                  borderRadius: 1,
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s',
                                  '&:hover': {
                                    backgroundColor: 'action.hover'
                                  },
                                  minHeight: '140px',
                                  maxWidth: '120px'
                                }}
                              >
                                <PdfIcon 
                                  sx={{ 
                                    fontSize: 64, 
                                    color: 'error.main',
                                    mb: 1
                                  }} 
                                />
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    textAlign: 'center',
                                    wordBreak: 'break-word',
                                    fontSize: '0.75rem',
                                    lineHeight: 1.2,
                                    maxWidth: '100%'
                                  }}
                                >
                                  {documentName}
                                </Typography>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    );
                  })()}
                </>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Dynamic Form Tabs */}
        {forms.map((form, index) => {
          const tabIndex = 4 + index; // Start after the static tabs (Overview, Appointments, Communication, Documents)
          return (
            <TabPanel key={form.id} value={tabValue} index={tabIndex}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  {loadingForms ? (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {console.log('[JobDetailPage] Rendering FormRenderer:', {
                        formCode: form.code,
                        hasJob: !!job,
                        hasCustomer: !!customer,
                        jobId: job?.id,
                        jobAddress: job?.jobAddress,
                        customerId: customer?.id
                      })}
                      <FormRenderer
                        form={form}
                        jobId={id}
                        jobNumber={job.jobNumber}
                        customerId={customer?.id}
                        assignedSalesPersonName={job.assignedSalesPersonName}
                        job={job}
                        customer={customer}
                        existingResponse={formResponses[form.id]}
                        isLoadingResponses={loadingFormResponses}
                        onSubmit={(responseData) => {
                        setSuccess('Form submitted successfully');
                        // Reload form responses after submission
                        if (id) {
                          loadFormResponses(form.id);
                        }
                      }}
                      />
                    </>
                  )}
                </CardContent>
              </Card>
            </TabPanel>
          );
        })}

        {/* Edit Job Dialog */}
        <BaseDialog 
          open={isEditDialogOpen} 
          onClose={() => setIsEditDialogOpen(false)}
          title="Edit Job"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              maxWidth: { xs: 'calc(100vw - 10px)', sm: '900px' }
            }
          }}
          contentSx={{ pb: 4 }}
          actions={
            <>
              <Button 
                onClick={() => setIsEditDialogOpen(false)}
                variant="outlined"
                color="primary"
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateJob} variant="contained">Update</Button>
            </>
          }
        >
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {customer && (
              <Grid item xs={12}>
                <CustomerDetailsCard
                  customer={customer}
                  title="Customer Details"
                />
              </Grid>
            )}
            <JobForm
              formData={formData}
              onFormDataChange={(data) => setFormData({ ...formData, ...data })}
              defaultBounds={{
                city: business?.defaultCity,
                country: business?.defaultCountry
              }}
              showCustomerSelection={false}
            />
          </Grid>
        </BaseDialog>
      </Box>
    </>
  );
};

export default JobDetailPage;
